import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "@ts-digital/vrc/es/components/modal";
import { reduce, take } from "lodash";
import numbro from "numbro";
import { useState } from "react";
import { FormattedDate, FormattedNumber } from "react-intl";
import { Schedule } from "../../../PaymentContainerClient/dtos/ConfirmSuccessPaymentResponseDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import getSchedulesSortedByChargeDate from "../../commons/getSchedulesSortedByChargeDate";
import { PrivacyCookiePolicyMenu } from "../../components/PrivacyCookiePolicyMenu";
import { CustomButton } from "../CustomButton";
import Tooltip from "../Tooltip";
import { useWindowDimensions } from "../WindowDimensionsProvider";
import firework from "./firework.svg";
import { Installment } from "./Installment";
import "./style.css";

const fm = getFormattedMessageWithScope("components.PaymentConfirmedSummary");

interface PaymentConfirmedSummaryProps {
    amount?: number;
    brand?: string;
    cardExpirationDate?: string;
    creditorName?: string;
    currency: string;
    installments?: Schedule[];
    isBankTransfer: boolean;
    isDownloadingReceipt: boolean;
    isPayNow: boolean;
    onDownloadReceipt: Function;
    onGoToPaymentRequest: Function;
    paymentReason?: string;
    panIban?: string;
    singleScheduledPayment?: Schedule;
}

export const PaymentConfirmedSummary = ({
    amount,
    brand,
    cardExpirationDate,
    creditorName,
    currency,
    installments,
    isBankTransfer,
    isDownloadingReceipt,
    isPayNow,
    onDownloadReceipt,
    onGoToPaymentRequest,
    paymentReason,
    panIban,
    singleScheduledPayment,
}: PaymentConfirmedSummaryProps) => {
    const { width } = useWindowDimensions();
    const [isInstallmentsModalOpen, setIsInstallmentsModalOpen] =
        useState(false);

    function toggleInstallmentsModal() {
        setIsInstallmentsModalOpen(!isInstallmentsModalOpen);
    }

    const sortedInstallments = getSchedulesSortedByChargeDate(installments);
    const first3Installments = take(sortedInstallments, 3);

    return (
        <div className="c-PaymentConfirmedSummary">
            <div className="c-PaymentConfirmedSummary-header">
                <img
                    src={firework}
                    alt="firework1"
                    className="c-PaymentConfirmedSummary-header-firework1-image"
                />
                <img
                    src={firework}
                    alt="firework2"
                    className="c-PaymentConfirmedSummary-header-firework2-image"
                />
                <span className="c-PaymentConfirmedSummary-title">
                    {fm("thankYou")}
                </span>
                <p className="c-PaymentConfirmedSummary-subtitle">
                    {fm(isPayNow ? "paymentSucceeded" : "paymentConfirmed")}
                </p>
            </div>
            <div className="c-PaymentConfirmedSummary-content">
                <div className="c-PaymentConfirmedSummary-content-heading">
                    <div className="c-PaymentConfirmedSummary-content-heading-firstLine">
                        <span className="c-PaymentConfirmedSummary-content-summary">
                            {fm("summary")}
                        </span>
                        <PrivacyCookiePolicyMenu />
                    </div>
                    <p className="c-PaymentConfirmedSummary-content-onBehalfOf">
                        {fm({
                            id: "onBehalfOf",
                            values: {
                                companyName: creditorName ? creditorName : "-",
                            },
                        })}
                    </p>
                </div>
                <div className="c-PaymentConfirmedSummary-content-paymentData">
                    <span className="c-PaymentConfirmedSummary-content-creditorName">
                        {creditorName ? creditorName : "-"}
                    </span>
                    <p className="c-PaymentConfirmedSummary-content-paymentReason-label">
                        {fm("paymentReason")}
                    </p>
                    <span className="c-PaymentConfirmedSummary-content-paymentReason-value">
                        {paymentReason || "-"}
                    </span>
                    <div className="c-PaymentConfirmedSummary-content-paymentData-amounts">
                        {isPayNow ? (
                            <p className="c-PaymentConfirmedSummary-content-paid-amount">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency}
                                    value={(amount || 0) / 100}
                                />
                            </p>
                        ) : installments ? (
                            <div>
                                {width > 800 ? (
                                    first3Installments.map(
                                        (installment, index) => (
                                            <Installment
                                                key={index}
                                                currency={currency}
                                                installmentNumber={index + 1}
                                                installment={installment}
                                            />
                                        )
                                    )
                                ) : (
                                    <Installment
                                        currency={currency}
                                        installmentNumber={1}
                                        installment={sortedInstallments[0]}
                                    />
                                )}
                                <CustomButton
                                    kind="tertiary"
                                    className="c-PaymentConfirmedSummary-content-installment-showInstallments-button"
                                    onClick={toggleInstallmentsModal}
                                >
                                    {fm("showInstallments")}
                                </CustomButton>
                            </div>
                        ) : singleScheduledPayment ? (
                            <div className="c-PaymentConfirmedSummary-content-singleScheduled-payment">
                                <p className="c-PaymentConfirmedSummary-content-paid-amount">
                                    <FormattedNumber
                                        style="currency"
                                        currency={currency}
                                        value={
                                            singleScheduledPayment.amount / 100
                                        }
                                    />
                                </p>
                                <span className="c-PaymentConfirmedSummary-content-singleScheduled-deadline">
                                    <div className="c-PaymentConfirmedSummary-content-singleScheduled-tooltip-container">
                                        <Tooltip
                                            text={fm("singleScheduledTooltip")}
                                        >
                                            <FontAwesomeIcon
                                                className="c-PaymentConfirmedSummary-content-singleScheduled-infoIcon"
                                                icon={faInfoCircle as IconProp}
                                            />
                                        </Tooltip>
                                    </div>
                                    {fm("deadline")}{" "}
                                    <FormattedDate
                                        value={
                                            singleScheduledPayment.chargeDate
                                        }
                                        year="numeric"
                                        month="2-digit"
                                        day="2-digit"
                                    />
                                </span>
                            </div>
                        ) : undefined}
                    </div>
                    <div className="c-PaymentConfirmedSummary-content-paymentData-method">
                        <span className="c-PaymentConfirmedSummary-content-paymentData-method-type">
                            {fm(
                                isBankTransfer
                                    ? "bankTransfer"
                                    : "creditOrDebitCard"
                            )}
                        </span>
                        <p className="c-PaymentConfirmedSummary-content-paymentData-method-data">
                            {isBankTransfer
                                ? fm({ id: "iban", values: { iban: panIban } })
                                : fm({
                                      id: "creditCard",
                                      values: { cardName: brand, pan: panIban },
                                  })}
                        </p>
                        <p className="c-PaymentConfirmedSummary-content-paymentData-method-data">
                            {isBankTransfer ? undefined : (
                                <>
                                    {fm("deadline")} {cardExpirationDate}
                                </>
                            )}
                        </p>
                    </div>
                </div>
                <span className="c-PaymentConfirmedSummary-content-paymentInfo">
                    {fm("paymentInfo")}
                </span>
                <div className="c-PaymentConfirmedSummary-content-buttons">
                    <CustomButton
                        kind="primary"
                        className="c-PaymentConfirmedSummary-content-buttons-button"
                        onClick={onGoToPaymentRequest}
                    >
                        {fm("goBackToPaymentRequest")}
                    </CustomButton>
                    <CustomButton
                        kind="tertiary"
                        className="c-PaymentConfirmedSummary-content-buttons-button"
                        onClick={onDownloadReceipt}
                        loading={isDownloadingReceipt}
                    >
                        {fm(
                            width > 800
                                ? "downloadPaymentReceipt"
                                : "downloadReceipt"
                        )}
                    </CustomButton>
                </div>
            </div>
            <Modal
                className="c-PaymentConfirmedSummary-installmentsModal"
                visible={isInstallmentsModalOpen}
                title={fm("modal.title")}
                onClose={toggleInstallmentsModal}
                footer={
                    <div className="c-PaymentConfirmedSummary-installmentsModal-footer">
                        <CustomButton
                            kind="primary"
                            onClick={toggleInstallmentsModal}
                        >
                            {fm("modal.close")}
                        </CustomButton>
                    </div>
                }
            >
                <div className="c-PaymentConfirmedSummary-installmentsModal-content">
                    {sortedInstallments.map((installment, index) => (
                        <Installment
                            key={index}
                            currency={currency}
                            installmentNumber={index + 1}
                            installment={installment}
                        />
                    ))}
                    <div className="c-PaymentConfirmedSummary-installmentsModal-content-total">
                        <div className="c-PaymentConfirmedSummary-installmentsModal-content-total-container">
                            <span className="c-PaymentConfirmedSummary-installmentsModal-content-total-label">
                                {fm("modal.total")}
                            </span>
                            <span className="c-PaymentConfirmedSummary-installmentsModal-content-total-value">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency}
                                    value={numbro(
                                        reduce(
                                            installments,
                                            (result, installment) =>
                                                numbro(result)
                                                    .add(installment.amount)
                                                    .value(),
                                            0
                                        )
                                    )
                                        .divide(100)
                                        .value()}
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
